import { getToken, saveToken } from './auth-storage.ts';
import { burnPortalCode, exchangePortalCode } from './portal-login.ts';
import { startPortalLogin, stripPortalParams, takePortalReturn, type PortalReturn } from './portal-flow.ts';

/**
 * ポータルから来た・戻ってきたときの受け取り。読み込んだ時点で1回だけ URL を読み、アドレス欄からすぐ消す（閲覧履歴に残さない）。
 * - portal_code    … 引換番号（2026-10-21〜・lib/portal-flow.ts）。このブラウザで始めたログインの続きなら、合言葉と一緒に API に出す
 * - portal_login=1 … ポータルのアプリ一覧から来た合図（証明書ではない）。ログインしていなければ、そのままログインを始める
 * - portal_token   … 旧方式のポータルの7日の証明書。2026-10-22 から受け取らない（API の /auth/portal も消した）。
 *                    古いブックマークや閲覧履歴から付いて来ても、ほかの portal_* と一緒に消すだけで、どこにも送らない
 * App より先に読み込まれる（main.tsx → App.tsx → hooks/useAuth.ts → ここ）ので、React Router が URL を読む前に消える。
 * ログイン済みのときも消す（2026-10-21 まではログイン済みだと portal_token がアドレス欄に残っていた）。
 */

/** このアプリのログイン（trendscope_token）が手元にあって、まだ期限内か（中身を読むだけ。確かめるのはサーバー） */
const hasFreshToken = (): boolean => {
  const token = getToken();
  if (!token) return false;
  try {
    const b64 = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))) as { exp?: unknown };
    return typeof claims.exp === 'number' && claims.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const initial = new URLSearchParams(window.location.search);
// ブラウザの保存場所が使えない（閉じられている）ときは、番号を受け取らずにログインの画面へ落とす
const safely = <T,>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};
const portalReturn: PortalReturn = safely(() => takePortalReturn(window.location.search), null);
/** ポータルのアプリ一覧から来た合図だけがあるとき（番号が無い）。ログインが無い・切れていたら、そのまま始めてよい */
export const canAutoStart = initial.get('portal_login') === '1' && !portalReturn;
stripPortalParams();

// このブラウザで始めていない番号は、すぐ使い切る（でたらめの合言葉で出すと、ポータルが使用済みにする）
if (portalReturn?.kind === 'invalid' && portalReturn.code) burnPortalCode(portalReturn.code);

let notice = portalReturn?.kind === 'invalid' ? 'ログインを最初からやり直してください' : '';
/** ログイン画面に出す一言（ポータルからのログインがうまくいかなかったとき。無ければ空） */
export const getPortalLoginNotice = (): string => notice;

export type ArrivalResult = 'none' | 'signed-in' | 'redirecting' | 'failed';

/** ポータルのログインを始める。始められなければ一言を残して failed */
export const startFromPortal = async (): Promise<ArrivalResult> => {
  try {
    await startPortalLogin();
    return 'redirecting';
  } catch {
    notice = 'ログインを始められませんでした。もう一度お試しください';
    return 'failed';
  }
};

// 失敗しても投げない（useAuth が loading のまま止まらないように）
const run = async (): Promise<ArrivalResult> => {
  try {
    if (portalReturn?.kind === 'code') {
      saveToken((await exchangePortalCode(portalReturn.code, portalReturn.verifier)).token);
      notice = '';
      return 'signed-in';
    }
    if (canAutoStart && !hasFreshToken()) return await startFromPortal();
    return 'none';
  } catch (err) {
    notice = err instanceof Error && err.message ? err.message : 'ログインに失敗しました';
    return 'failed';
  }
};

let pending: Promise<ArrivalResult> | null = null;

/** ポータルから持ってきたものを、このアプリのログインに替える。React StrictMode で2回呼ばれても、番号を出すのは1回だけ */
export const finishPortalArrival = (): Promise<ArrivalResult> => {
  pending ??= run();
  return pending;
};
